<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import {
  TSAssetInfo,
  TSDialogForm,
  TSDropdown,
  TSIcon,
  useLoadingStore,
  useToast,
} from 'tsv2-library';
import { DropdownOption } from 'tsv2-library/dist/src/types/options';

import BorrowingServices from '@/services/borrowing.service';

import {
  BorrowingBorrowedByAsset,
  BorrowingTransactionRequest,
} from '@/types/borrowing.type';
import { FormPayload } from 'tsv2-library/dist/src/components/v2/Form/Form.vue';

const toast = useToast();
const router = useRouter();
const { setLoading } = useLoadingStore();

const visible = defineModel<boolean>('visible', { required: true });

const props = defineProps<{
  selectedDatum?: BorrowingTransactionRequest | BorrowingBorrowedByAsset;
}>();

const actionModel = ref<string>();

const actionOptions = computed<DropdownOption[]>(() => {
  const options: DropdownOption[] = [
    {
      label: 'Request Extension',
      value: 'Request Extension',
      visible: true,
    },
    {
      label: 'Return to available',
      value: 'Return to available',
      visible: true,
    },
    {
      label: 'Back to borrowed asset',
      value: 'Back to borrowed asset',
      visible: (props.selectedDatum?.remainingDay ?? 0) > 0,
    },
  ];

  return options.filter((option) => option.visible);
});

/**
 * Handle the asset confirmation
 */
const handleConfirmation = async (payload: FormPayload): Promise<void> => {
  if (!props.selectedDatum) return;
  try {
    setLoading(true);

    await BorrowingServices.putConfirmReportDone(props.selectedDatum._id, {
      status: 'On Borrowing',
    });

    toast.add({
      message: 'Success, asset confirmation has been submited.',
      severity: 'success',
    });

    switch (payload.formValues.action) {
      case 'Request Extension':
        sessionStorage.setItem(
          'assetBorrowRequestExtension',
          JSON.stringify([props.selectedDatum._id]),
        );

        router.push({
          name: 'AssetBorrowTransactionExtensionRequestView',
        });
        break;
      case 'Return to available':
        sessionStorage.setItem(
          'assetBorrowReturn',
          JSON.stringify([props.selectedDatum._id]),
        );

        router.push({
          name: 'AssetBorrowBorrowedReturnAssetView',
        });
        break;
      case 'Back to borrowed asset':
        router.push({
          name: 'AssetBorrowBorrowedByAssetView',
        });
        break;
    }

    dispatchEvent(
      new CustomEvent('updateTable', {
        detail: {
          tableName: 'borrowed-by-asset-table',
        },
      }),
    );
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to submit asset confirmation.',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
};
</script>
<template>
  <TSDialogForm
    v-if="selectedDatum"
    v-model:visible="visible"
    :header="
      selectedDatum.status === 'Reported Missing'
        ? 'Asset Found Confirmation'
        : 'Asset Repaired Confirmation'
    "
    :reset-after-submit="false"
    :show-stay-checkbox="false"
    :use-clear-btn="false"
    @hide="actionModel = undefined"
    @submit="handleConfirmation"
    action="Submit"
    closable
    data-ts-name="asset-confirmation"
    width="semi-xlarge"
  >
    <template #fields>
      <TSAssetInfo
        :id="selectedDatum.asset"
        :fields="['brand', 'model', 'group']"
      />
      <div class="flex justify-between items-center">
        <div class="flex items-center gap-2">
          <span>Borrower:</span>
          <div class="flex items-center gap-[2px]">
            <TSIcon class="h-4 w-4 text-general-400" icon="user" />
            <span class="text-primary-500">
              {{ selectedDatum.borrower.fullName }}
            </span>
          </div>
        </div>
        <span class="font-medium text-xs">
          Remaining Time:
          <span
            :class="
              selectedDatum.remainingDay > -1 ? undefined : 'text-danger-500'
            "
          >
            {{ selectedDatum.remainingDay }} Day(s)
          </span>
        </span>
      </div>
      <TSDropdown
        v-model="actionModel"
        :options="actionOptions"
        data-ts-section="action-input"
        field-name="action"
        label="Action"
        mandatory
        option-label="label"
        option-value="value"
        placeholder="Select action"
        use-validator
      />
    </template>
  </TSDialogForm>
</template>
